import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { CalendarClock, Sparkles, ArrowUpRight, AlertTriangle, FileText, MessageSquareText } from 'lucide-react';
import { severityStyles } from '@/components/common';

const REF_DAY = 8; // reference "today" in June 2026

export const RightPanel = () => {
  const { t, tx, data, setAdvisorOpen } = useApp();
  const navigate = useNavigate();
  if (!data) return null;

  const upcoming = data.obligations.slice(0, 4);
  const attention = data.todayAttention.map((id) => data.alerts.find((a) => a.id === id)).filter(Boolean).slice(0, 3);
  const activity = data.notifications.slice(0, 4);
  const advisor = data.team[0];

  const dayLabel = (day) => {
    const diff = day - REF_DAY;
    if (diff <= 0) return t('panel.today');
    return `${diff} ${t('panel.days')}`;
  };

  return (
    <aside className="hidden xl:flex flex-col fixed inset-y-0 right-14 w-[300px] z-20 pt-16 overflow-y-auto sidebar-scroll" data-testid="right-panel">
      <div className="p-5 space-y-4">
        {/* Próximamente */}
        <Section title={t('panel.upcoming')} icon={CalendarClock} onView={() => navigate('/app/calendar')}>
          <div className="space-y-2.5">
            {upcoming.map((o) => (
              <button key={o.id} onClick={() => navigate('/app/calendar')} data-testid={`panel-upcoming-${o.id}`} className="w-full flex items-center gap-3 text-left group">
                <div className="flex flex-col items-center justify-center w-9 h-9 rounded-lg bg-cyan-50 text-cyan-700 shrink-0">
                  <span className="text-sm font-bold leading-none font-heading">{o.day}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-zinc-800 leading-snug truncate group-hover:text-cyan-700 transition-colors">{tx(o.title)}</p>
                  <p className="text-[11px] text-zinc-400">{dayLabel(o.day)}</p>
                </div>
              </button>
            ))}
          </div>
        </Section>

        {/* Requiere atención */}
        <Section title={t('panel.attention')} icon={AlertTriangle} onView={() => navigate('/app/alerts')}>
          <div className="space-y-2">
            {attention.map((a) => {
              const s = severityStyles[a.severity];
              return (
                <button key={a.id} onClick={() => navigate('/app/alerts')} data-testid={`panel-attention-${a.id}`} className={`w-full text-left rounded-lg border ${s.border} ${s.bg} p-2.5`}>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                    <span className={`text-[9px] font-bold uppercase tracking-wider ${s.text}`}>{t(`alerts.${a.severity}`)}</span>
                  </div>
                  <p className="text-[13px] font-medium text-zinc-800 leading-snug">{tx(a.title)}</p>
                </button>
              );
            })}
          </div>
        </Section>

        {/* Insights / Advisor */}
        <div className="rounded-xl p-4 relative overflow-hidden text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #0891B2 0%, #6D28D9 100%)' }}>
          <div className="grain absolute inset-0 opacity-20" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4" />
              <span className="text-xs font-bold uppercase tracking-widest text-white/80">{t('panel.insights')}</span>
            </div>
            <div className="flex items-center gap-2.5 mb-3 mt-3">
              <div className="w-9 h-9 rounded-full overflow-hidden bg-white/20 shrink-0">
                {advisor.avatar ? <img src={advisor.avatar} alt={advisor.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center font-semibold">{advisor.name[0]}</div>}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold leading-tight">{advisor.name}</p>
                <p className="text-[11px] text-white/70 truncate">{tx(advisor.role)}</p>
              </div>
            </div>
            <button onClick={() => setAdvisorOpen(true)} data-testid="panel-advisor-btn" className="w-full flex items-center justify-center gap-2 h-9 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-medium transition-colors">
              <MessageSquareText className="w-4 h-4" /> {t('panel.askAdvisor')}
            </button>
          </div>
        </div>

        {/* Actividad reciente */}
        <Section title={t('panel.activity')} icon={null} onView={() => navigate('/app/notifications')}>
          <div className="space-y-3">
            {activity.map((n) => (
              <div key={n.id} className="flex items-start gap-2.5" data-testid={`panel-activity-${n.id}`}>
                <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${n.unread ? 'bg-cyan-500' : 'bg-zinc-300'}`} />
                <div className="min-w-0">
                  <p className="text-[13px] text-zinc-600 leading-snug">{tx(n.text)}</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">{n.time}</p>
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </aside>
  );
};

const Section = ({ title, icon: Icon, onView, children }) => {
  const { t } = useApp();
  return (
    <div className="rounded-xl border border-white/80 bg-white/[0.96] backdrop-blur-xl p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          {Icon && <Icon className="w-3.5 h-3.5 text-zinc-400" />}
          <h4 className="text-[10px] tracking-[0.18em] uppercase font-bold text-zinc-500">{title}</h4>
        </div>
        {onView && <button onClick={onView} className="text-cyan-600 text-[11px] font-medium hover:underline">{t('common.viewAll')}</button>}
      </div>
      {children}
    </div>
  );
};
